import { useEffect, useState, useSyncExternalStore } from 'react';
import { Alert, Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { formatDate, formatTime, type CommitResponse, type UndoLastOp, type UndoLastResponse } from '@parts/shared';
import { errorText, getJson, postJson } from '../api';
import { useI18n } from '../i18n';
import { useAppState } from '../state';
import { Avatar } from './ui';

/**
 * Отмена последнего действия (только администратор).
 * Открывается из меню роли в боковой панели и с экрана «История»; окно одно на всё приложение.
 */
let opened = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function openUndoDialog(): void {
  opened = true;
  notify();
}

function closeUndoDialog(): void {
  opened = false;
  notify();
}

function useOpened(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => opened
  );
}

export function UndoLastActionDialog() {
  const { t, tx } = useI18n();
  const isOpen = useOpened();
  const { apply } = useAppState();
  const [op, setOp] = useState<UndoLastOp | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setOp(undefined);
    setError(null);
    getJson<UndoLastResponse>('/api/undo/last')
      .then((res) => setOp(res.op))
      .catch((err) => setError(errorText(err)));
  }, [isOpen]);

  const confirm = async () => {
    if (!op) return;
    setBusy(true);
    try {
      const res = await postJson<CommitResponse>('/api/undo', { id: op.id });
      if (!res.ok) {
        notifications.show({ color: 'red', title: t('Не отменено'), message: tx(res.message) });
        return;
      }
      apply(res.state);
      closeUndoDialog();
      notifications.show({ color: 'green', title: t('Действие отменено'), message: tx(res.message), autoClose: 8000 });
    } catch (err) {
      notifications.show({ color: 'red', title: t('Ошибка'), message: tx(errorText(err)) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={isOpen} onClose={closeUndoDialog} title={t('Отменить последнее действие')} size="md">
      {error ? (
        <Alert color="red" variant="light">
          {tx(error)}
        </Alert>
      ) : op === undefined ? (
        <Group gap={10} c="dimmed">
          <Loader size="sm" />
          {t('Загружаю данные…')}
        </Group>
      ) : op === null ? (
        <Text size="sm" c="dimmed">
          {t('Отменять нечего: проведённых действий нет.')}
        </Text>
      ) : (
        <Stack gap="md">
          <div style={{ padding: '12px 14px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }}>
            <Text fw={600}>{tx(op.title)}</Text>
            <Group gap={8} mt={6}>
              <Avatar name={tx(op.author) || '?'} size={20} />
              <Text size="sm" c="dimmed">
                {tx(op.author)} · {formatDate(op.at)}, {formatTime(op.at)}
              </Text>
            </Group>
          </div>
          <Text size="sm" c="dimmed">
            {t('Данные вернутся к состоянию до этого действия: запросы — к прежнему составу, добавленные записи будут удалены. Отменять можно по одному действию, начиная с последнего.')}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeUndoDialog}>
              {t('Отмена')}
            </Button>
            <Button color="red" onClick={() => void confirm()} loading={busy}>
              {t('Отменить действие')}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
